import { useEffect, useRef, useState, useCallback } from "react";

// WebSocket client hook with auto-reconnect.
// Usage:
//   const { status, subscribe, send } = useWebSocket({
//     onMessage: (msg) => { ... }
//   });
//   useEffect(() => { if (status === 'open') subscribe(['order:xyz']); }, [status]);

export default function useWebSocket({ onMessage, autoConnect = true } = {}) {
  const [status, setStatus] = useState("closed"); // closed | connecting | open | error
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedByUserRef = useRef(false);
  const pendingSubsRef = useRef(new Set());
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const buildUrl = () => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");
    const token = localStorage.getItem("talabya_token") || "";
    return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
  };

  const connect = useCallback(() => {
    // Allow both authenticated and anonymous connections (backend accepts empty token and
    // subscribes anonymous clients to the public 'restaurants' topic for live menu updates).
    if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;
    closedByUserRef.current = false;
    setStatus("connecting");
    const ws = new WebSocket(buildUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("open");
      retryRef.current = 0;
      // Flush pending subs
      if (pendingSubsRef.current.size > 0) {
        ws.send(JSON.stringify({ type: "subscribe", topics: Array.from(pendingSubsRef.current) }));
      }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        onMessageRef.current?.(msg);
      } catch { /* ignore malformed frames */ }
    };
    ws.onerror = () => { setStatus("error"); };
    ws.onclose = () => {
      setStatus("closed");
      wsRef.current = null;
      if (!closedByUserRef.current) {
        const delay = Math.min(1000 * 2 ** retryRef.current, 15000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      }
    };
  }, []);

  const subscribe = useCallback((topics) => {
    topics.forEach((t) => pendingSubsRef.current.add(t));
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: "subscribe", topics }));
    }
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj));
  }, []);

  const disconnect = useCallback(() => {
    closedByUserRef.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (autoConnect) connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);

  return { status, subscribe, send, connect, disconnect };
}
